import { usePageSeo } from '@/hooks/usePageSeo';
import { SITE, SITE_URL } from '@/config/site';
import { voices } from '@/mocks/voices';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Reveal from '@/components/base/Reveal';
import SectionTitle from '@/components/base/SectionTitle';
import ContactForm from '@/pages/about/components/ContactForm';

const principles = [
  {
    icon: 'ri-compass-3-line',
    title: '只梳理，不站队',
    desc: '我们把讨论中的立场呈现出来，但不替任何一方下结论。分歧本身就是值得被看见的信息。',
  },
  {
    icon: 'ri-links-line',
    title: '先溯源，再传播',
    desc: '围绕话题尽量回到最早出现的信息与关键节点，减少在转述中走样的可能。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '尊重边界',
    desc: '涉及个人隐私、未证实指认的内容我们会做处理，不把讨论变成针对个体的攻击。',
  },
  {
    icon: 'ri-refresh-line',
    title: '有进展就更新',
    desc: '话题是流动的。新的公开进展会补进时间线，发现错误会在原位标注更正。',
  },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${SITE.name}`,
    url: `${SITE_URL}/about`,
    inLanguage: 'zh-CN',
    description: SITE.intro,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: SITE_URL,
    email: SITE.email,
    description: SITE.intro,
    foundingDate: SITE.since,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: SITE.email,
      availableLanguage: ['zh-CN'],
    },
  },
];

export default function About() {
  usePageSeo({
    title: '关于我们｜吃瓜观察局的定位与内容原则',
    description:
      '了解吃瓜观察局：我们是一个聚焦网络热点话题的观察型站点，坚持只梳理不站队、先溯源再传播、尊重边界、有进展就更新的内容原则，欢迎留下你的意见。',
    keywords: '吃瓜观察局,关于我们,内容原则,舆论观察,话题讨论',
    path: '/about',
    jsonLd,
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-secondary-50">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-5 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            我们不做情绪放大器，只想把来龙去脉讲清楚
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">{SITE.intro}</p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-4 md:px-6 lg:grid-cols-2">
          <Reveal>
            <div className="w-full overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20abstract%20scene%20of%20many%20small%20light%20dots%20connected%20by%20glowing%20orange%20lines%20on%20cream%20backdrop%2C%20minimal%20network%20metaphor%2C%20soft%20artistic%20style%2C%20no%20text&width=1200&height=800&seq=about-observe-01&orientation=landscape"
                alt="用连线把分散的信息串联起来的抽象示意图"
                className="h-64 w-full object-cover md:h-80"
              />
            </div>
          </Reveal>
          <Reveal delay={80}>
            <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              <span className="h-3 w-1 rounded-full bg-primary-500" />
              Our Story
            </p>
            <h2 className="text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {SITE.since} 年至今，我们只做了一件事
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              最初只是想弄明白：为什么同一件事，不同的人会得出完全相反的结论。后来我们发现，很多时候分歧并不来自事实，而是来自信息到达每个人的顺序不一样。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              于是我们开始尝试把话题按时间线整理，把不同立场归类。慢慢地，这个小站点成了不少人的"补课入口"——刷到热搜时先来这里看一眼，再决定要不要下场讨论。
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[
                { value: '1,286', label: '观察过的话题' },
                { value: '5 年', label: '持续更新' },
                { value: '6 大', label: '内容分类' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <dt className="font-heading text-xl font-bold text-primary-600">{s.value}</dt>
                  <dd className="mt-1 text-xs text-foreground-500">{s.label}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <Reveal>
            <SectionTitle
              eyebrow="Principles"
              title="四条内容原则"
              desc="这些原则写在我们每一次内容整理的流程里，也接受大家监督。"
            />
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 50}>
                <article className="flex h-full gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-2xl text-accent-800">
                    <i className={p.icon} />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <Reveal>
            <SectionTitle eyebrow="Voices" title="大家怎么说" desc="来自一些长期围观者的反馈，我们会认真看每一条。" />
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {voices.map((v, i) => (
              <Reveal key={v.name} delay={i * 40} className="h-full">
                <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                  <i className="ri-double-quotes-l text-2xl text-primary-300" />
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{v.content}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-sm font-medium text-secondary-800">
                      {v.name.slice(0, 1)}
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-foreground-900">{v.name}</span>
                      <span className="text-xs text-foreground-400">{v.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-accent-50 py-14 md:py-20" id="contact">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                <span className="h-3 w-1 rounded-full bg-primary-500" />
                Contact
              </p>
              <h2 className="text-2xl font-bold leading-snug text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                无论是内容更正、话题推荐还是合作洽谈，都欢迎给我们留言。涉及事实更正与隐私问题的反馈，我们会优先处理。
              </p>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                    <i className="ri-mail-line" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs text-foreground-500">内容与反馈</span>
                    <a href={`mailto:${SITE.email}`} className="cursor-pointer text-foreground-900 hover:text-primary-600">
                      {SITE.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700">
                    <i className="ri-briefcase-line" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs text-foreground-500">合作洽谈</span>
                    <a href={`mailto:${SITE.contactEmail}`} className="cursor-pointer text-foreground-900 hover:text-primary-600">
                      {SITE.contactEmail}
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                    <i className="ri-time-line" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs text-foreground-500">回复时间</span>
                    <span className="text-foreground-900">工作日 10:00 - 18:00</span>
                  </span>
                </li>
              </ul>
            </Reveal>
          </div>

          <Reveal delay={80} className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h3 className="text-base font-semibold text-foreground-950">给我们留言</h3>
              <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项，留言内容请控制在 500 字以内。</p>
              <div className="mt-5">
                <ContactForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}